// React hook for viewport service
import { useEffect, useMemo, useRef, useState } from 'react';

import { ViewportConfigIn } from './types/viewport-config.type';
import { ViewportServiceClass } from './viewport-service';
import { UseViewportReturn, ViewportActions, ViewportState } from './types';

export const useViewport = (config?: Partial<ViewportConfigIn>, renderCallback?: () => void): UseViewportReturn => {
  const serviceRef = useRef<ViewportServiceClass>(new ViewportServiceClass(config, renderCallback));

  const [state, setState] = useState<ViewportState>(() => {
    const service = serviceRef.current!;
    return {
      zoom: service.state.zoom,
      rotation: service.state.rotation,
      xPan: service.state.xPan,
      yPan: service.state.yPan,
      targetZoom: service.state.targetZoom,
      targetRotation: service.state.targetRotation,
      targetXPan: service.state.targetXPan,
      targetYPan: service.state.targetYPan,
      rotationHistory: service.state.rotationHistory
    };
  });

  useEffect(() => {
    const service = serviceRef.current!;
    const unsubscribe = service.subscribe(setState);
    return unsubscribe;
  }, []);

  const actions: ViewportActions = useMemo(() => ({
    setPan: (x: number, y: number) => {
      serviceRef.current?.actions.setPan(x, y);
    },
	smoothPan: (x: number, y: number, duration?: number) => {
      serviceRef.current?.actions.smoothPan(x, y, duration);
    },
    cancelPanAnimation: () => {
      serviceRef.current?.actions.cancelPanAnimation();
    },
    resetView: () => {
      serviceRef.current?.actions.resetView();
    },
    resetSizeState: () => {
      serviceRef.current?.actions.resetSizeState();
    },
    fitToView: (contentBounds?: { x?: number; y?: number; width: number; height: number }) => {
      serviceRef.current?.actions.fitToView(contentBounds);
    },
    setZoom: (zoom: number) => {
		serviceRef.current?.actions.setZoom(zoom);
	  },
	smoothZoom: (targetZoom: number, duration?: number) => {
      serviceRef.current?.actions.smoothZoom(targetZoom, duration);
    },
    cancelZoomAnimation: () => {
      serviceRef.current?.actions.cancelZoomAnimation();
    },
    setRotation: (rotation: number) => {
      serviceRef.current?.actions.setRotation(rotation);
    },
    smoothRotation: (targetRotation: number, duration?: number) => {
      serviceRef.current?.actions.smoothRotation(targetRotation, duration);
    },
    cancelRotationAnimation: () => {
      serviceRef.current?.actions.cancelRotationAnimation();
    },
    updateSize: (width: number, height: number) => {
      serviceRef.current?.actions.updateSize(width, height);
    },
    transformCoordinates: (worldX: number, worldY: number) => {
      return serviceRef.current?.actions.transformCoordinates(worldX, worldY) || { x: worldX, y: worldY };
    },
    getSvgTransform: () => {
      return serviceRef.current?.actions.getSvgTransform() || '';
    },
    getSvgElement: () => {
      return serviceRef.current?.actions.getSvgElement() || null;
    },
    registerSvg: (svgElement: SVGElement) => {
      serviceRef.current?.actions.registerSvg(svgElement);
    },
    unregisterSvg: () => {
      serviceRef.current?.actions.unregisterSvg();
    },
    getEquivalentRotation: () => {
      return serviceRef.current?.actions.getEquivalentRotation() || {
        angleRadians: 0,
        angleDegrees: 0,
        centerX: 0,
        centerY: 0,
        isPureTranslation: true
      };
    },
    triggerAutoZoom: () => {
      serviceRef.current?.actions.triggerAutoZoom();
    }
  }), []);

  // Additional viewport service properties
  const viewportService = useMemo(() => serviceRef.current!, []);

  return {
    state,
    actions,
    // Direct access to viewport service properties
    config: viewportService.config,
    isZooming: viewportService.isZooming,
    isRotating: viewportService.isRotating,
	isPanning: viewportService.isPanning,
    containerSize: viewportService.containerSize,
    viewerSize: viewportService.viewerSize
  };
};
